import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import SEO from '../../components/SEO';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import StatCard from '../../components/ui/StatCard';
import { supportAPI } from '../../services/supportAPI';
import { stationsAPI } from '../../services/api';
import { Activity, Ticket, Clock, CheckCircle, Search, Filter } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const SupportDashboard = () => {
    const { admin } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stations, setStations] = useState([]);
    const [selectedStation, setSelectedStation] = useState('');

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                if (admin?.role === 'super_admin') {
                    const stRes = await stationsAPI.getAll();
                    setStations(stRes.data.stations);
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchInitialData();
    }, [admin]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const res = await supportAPI.getDashboard({ station: selectedStation });
                setStats(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, [selectedStation]);

    if (loading && !stats) return (
        <AdminLayout>
            <div className="flex justify-center items-center h-[80vh]"><LoadingSpinner size="lg" /></div>
        </AdminLayout>
    );

    return (
        <AdminLayout>
            <SEO title="Support Dashboard | Super Admin" />
            
            <div className="p-6 max-w-7xl mx-auto space-y-6">
                
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-display font-bold text-midnight-900">Support Dashboard</h1>
                        <p className="text-midnight-500 text-sm">Analytics and recent activity for support tickets</p>
                    </div>

                    <div className="flex items-center gap-3">
                        {admin?.role === 'super_admin' && (
                            <select 
                                className="input-field py-2 text-sm w-48"
                                value={selectedStation}
                                onChange={(e) => setSelectedStation(e.target.value)}
                            >
                                <option value="">All Stations</option>
                                {stations.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                            </select>
                        )}
                        <Link to="/superadmin/support/tickets" className="btn-primary py-2 text-sm shadow-none">
                            View All Tickets
                        </Link>
                    </div>
                </div>

                {/* Stats Grid */}
                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard 
                            title="Total Tickets" 
                            value={stats.stats.total} 
                            icon={Ticket} 
                            trend={null}
                            color="blue"
                        />
                        <StatCard 
                            title="Open / Waiting" 
                            value={stats.stats.open} 
                            icon={Clock} 
                            trend={null}
                            color="orange"
                        />
                        <StatCard 
                            title="In Progress" 
                            value={stats.stats.inProgress} 
                            icon={Activity} 
                            trend={null}
                            color="yellow"
                        />
                        <StatCard 
                            title="Resolved / Closed" 
                            value={stats.stats.resolved + stats.stats.closed} 
                            icon={CheckCircle} 
                            trend={null}
                            color="green"
                        />
                    </div>
                )}

                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Category Breakdown */}
                    <div className="lg:col-span-1">
                        <div className="card p-6 h-full">
                            <h3 className="font-display font-bold text-lg text-midnight-900 mb-6">Tickets by Category</h3>
                            
                            <div className="space-y-4">
                                {stats?.byCategory?.length > 0 ? stats.byCategory.sort((a, b) => b.count - a.count).map(cat => {
                                    const percentage = Math.round((cat.count / stats.stats.total) * 100);
                                    return (
                                        <div key={cat._id}>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="font-medium text-midnight-800">{cat._id}</span>
                                                <span className="text-midnight-500">{cat.count} ({percentage}%)</span>
                                            </div>
                                            <div className="w-full bg-midnight-50 rounded-full h-2">
                                                <div className="bg-railway-500 h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <p className="text-midnight-400 text-sm text-center py-4">No data available</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="lg:col-span-2">
                        <div className="card p-6 h-full flex flex-col">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-display font-bold text-lg text-midnight-900">Recent Support Activity</h3>
                            </div>

                            <div className="flex-grow overflow-y-auto pr-2" style={{ maxHeight: '400px' }}>
                                {stats?.recentActivity?.length > 0 ? (
                                    <div className="space-y-4">
                                        {stats.recentActivity.map((act) => (
                                            <div key={act._id} className="relative pl-6 before:absolute before:inset-0 before:left-[11px] before:w-0.5 before:bg-midnight-100 pb-2">
                                                <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-midnight-50 border-2 border-white flex items-center justify-center text-midnight-400 shadow-sm z-10">
                                                    <div className="w-2 h-2 rounded-full bg-railway-400" />
                                                </div>
                                                <div className="bg-white border border-midnight-100 p-3 rounded-lg shadow-sm">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <div className="text-sm font-semibold text-midnight-900">
                                                            {act.action} <span className="font-normal text-midnight-500 ml-1">on</span> <Link to={`/superadmin/support/${act.ticket?._id}`} className="text-railway-600 hover:underline">{act.ticket?.ticketNumber}</Link>
                                                        </div>
                                                        <div className="text-[10px] text-midnight-400 whitespace-nowrap">
                                                            {new Date(act.createdAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>
                                                    {act.performedBy?.name && (
                                                        <div className="text-xs text-midnight-500">
                                                            by <span className="font-medium text-midnight-700">{act.performedBy.name}</span> {act.performedByType === 'customer' ? '(Customer)' : '(Admin)'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full py-10">
                                        <Activity className="w-12 h-12 text-midnight-200 mb-3" />
                                        <p className="text-midnight-500 text-sm">No recent activity found.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </AdminLayout>
    );
};

export default SupportDashboard;
